import {INFLUENCER_KEYS} from "@/api/influencerApi";
import type {InfluencerProfileDetailSerializer} from "@/generated/Influencer";
import {useIsAgency, useSelectedCompany} from "@/hooks/useUserHooks";
import {queryClient} from "@/queryClient";
import {COUNTRY_FILTERS} from "@/store/constants";
import {create} from "@/store/createStore";
import type {
  TCampaignType,
  TCountry,
  TInfluencerAward,
  TNonFunctionKeys,
  TOpenCampaign,
  TPaginated,
  TPunishmentType,
} from "@/store/types";
import useUserStore from "@/store/user";
import {type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {DebouncedFuncLeading, debounce, omit} from "lodash";
import qs from "qs";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

export type TFilters = {
  age_range_start: number[];
  audience_gender: string | null;
  audience_country: string | null;
  audience_region_id: number[];
  audience_city: string;
  gender: string[];
  punishment_type: TPunishmentType | null;
  search: string;
  engagement_start: number;
  engagement_end: number;
  median_views_start: number;
  median_views_end: number;
  tiktok_last_period_video_count_start: number;
  tiktok_last_period_video_count_end: number;
  followers_start: number;
  followers_end: number;
  location: string | null;
  region_id: number[];
  city: string;
  interests: number[];
  ordering:
    | "-user__last_app_login"
    | "-created"
    | "-engagement"
    | "-followers"
    | "-rating"
    | "-recommended"
    | "favourites_first"
    | "-median_views"
    | null;
  type: TCampaignType;
  award: TInfluencerAward | null;
  recommended: boolean | null;
  favourites_first: boolean | null;
};

export type TInitialFilters = {
  followers__min: number;
  followers__max: number;
  engagement__min: number;
  engagement__max: number;
  median_views__min: number;
  median_views__max: number;
  tiktok_last_period_video_count__min: number;
  tiktok_last_period_video_count__max: number;
  ages: number[];
  followers__histogram: {range: number; value: number}[];
  engagement__histogram: {range: number; value: number}[];
  median_views__histogram: {range: number; value: number}[];
  tiktok_last_period_video_count__histogram: {range: number; value: number}[];
  count: number;
  swiss_regions: {id: number; name_de: string; name: string}[];
  german_regions: {id: number; name_de: string; name: string}[];
  high_density_regions: {
    id: number;
    name: string;
    influencer_count: number;
    country: string;
    name_de: string;
  }[];
  cities: {name: string; country__name: string}[];
};

type TState = {
  searchParams: string | null;
  initialFilters: TInitialFilters;
  filters: TFilters;
  openCampaignsOfCompany: TOpenCampaign[];
  debouncedSearchParams: DebouncedFuncLeading<() => void>;
  buildSearchParams(): string;
  setInitialFilters(filters: TState["initialFilters"]): void;
  setSearchParams(searchParams: string): void;
  setFilters<K extends keyof TFilters, V extends TFilters[K]>(key: K, value: V, options?: {skipQuery: boolean}): void;
  setValue<K extends TNonFunctionKeys<TState>, V extends TState[K]>(key: K, value: V): void;
  getHistogram<
    K extends
      | "followers__histogram"
      | "engagement__histogram"
      | "median_views__histogram"
      | "tiktok_last_period_video_count__histogram",
  >(
    key: K,
  ): TState["initialFilters"][K];
  reset(): void;
};

const initialState = {
  searchParams: null,
  initialFilters: {
    followers__min: 0,
    followers__max: 0,
    engagement__min: 0,
    engagement__max: 0,
    median_views__min: 0,
    median_views__max: 0,
    tiktok_last_period_video_count__min: 0,
    tiktok_last_period_video_count__max: 0,
    followers__histogram: [],
    engagement__histogram: [],
    median_views__histogram: [],
    tiktok_last_period_video_count__histogram: [],
    count: 0,
    swiss_regions: [],
    german_regions: [],
    high_density_regions: [],
    cities: [],
    ages: [],
  },
  filters: {
    age_range_start: [],
    audience_gender: null,
    audience_country: null,
    audience_region_id: [],
    audience_city: "",
    gender: [],
    punishment_type: null,
    search: "",
    engagement_start: 0,
    engagement_end: 0,
    median_views_start: 0,
    median_views_end: 0,
    tiktok_last_period_video_count_start: 0,
    tiktok_last_period_video_count_end: 0,
    followers_start: 0,
    followers_end: 0,
    location: null,
    region_id: [],
    city: "",
    interests: [],
    ordering: "-recommended",
    type: "instagram",
    award: null,
    recommended: null,
    favourites_first: null,
  },
  openCampaignsOfCompany: [],
} as Pick<TState, TNonFunctionKeys<TState>>;

const useInfluencerNetworkStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        debouncedSearchParams: debounce(() => {
          get().buildSearchParams();
        }, 1000),
        buildSearchParams: () => {
          const isAdmin = useUserStore.getState().user.user_type === "admin";

          if (!get().filters.ordering) {
            set(state => {
              state.filters.ordering = "-recommended";
            });
          }

          if (get().filters.ordering === "-recommended") {
            set(state => {
              state.filters.recommended = true;
              state.filters.favourites_first = null;
            });
          } else if (get().filters.ordering === "favourites_first") {
            set(state => {
              state.filters.favourites_first = true;
              state.filters.recommended = null;
            });
          } else {
            set(state => {
              state.filters.recommended = null;
              state.filters.favourites_first = null;
            });
          }

          const filters = get().filters;
          const filtersWithoutRecommendedAndFavourites = omit(filters, ["recommended", "favourites_first"]);
          const params = isAdmin
            ? filtersWithoutRecommendedAndFavourites
            : get().filters.ordering === "-recommended" || get().filters.ordering === "favourites_first"
              ? {...filters, ordering: null}
              : filters;

          const omittedParams = omit(
            params,
            filters.type === "tiktok"
              ? ["engagement_start", "engagement_end"]
              : ["tiktok_last_period_video_count_start", "tiktok_last_period_video_count_end"],
          );

          return qs.stringify(omittedParams, {arrayFormat: "comma"});
        },
        setSearchParams: searchParams =>
          set(
            state => {
              state.searchParams = searchParams;
            },
            false,
            "influencerNetworkStore/buildSearchParams",
          ),
        setInitialFilters: filters => {
          const user = useUserStore.getState().user;
          const isAgency = useIsAgency();
          const isAdmin = user.user_type === "admin";
          const isCompany = user.is_company;
          const selectedCompany = useSelectedCompany();
          const selectedAgency = useUserStore.getState().user.agencies?.at(0)?.agency;

          set(
            state => {
              state.initialFilters = {
                ...state.initialFilters,
                ...filters,
              };

              if (state.filters.type !== "ugc") {
                state.filters.followers_start = filters.followers__min;
                state.filters.followers_end = filters.followers__max;
                state.filters.median_views_start = filters.median_views__min;
                state.filters.median_views_end = filters.median_views__max;

                if (state.filters.type === "tiktok") {
                  state.filters.tiktok_last_period_video_count_start = filters.tiktok_last_period_video_count__min;
                  state.filters.tiktok_last_period_video_count_end = filters.tiktok_last_period_video_count__max;
                } else {
                  state.filters.engagement_start = filters.engagement__min;
                  state.filters.engagement_end = filters.engagement__max;
                }
              }

              if (!get().filters.location && isCompany && !isAdmin) {
                const country = isAgency ? selectedAgency?.country : selectedCompany?.country;

                state.filters.location = COUNTRY_FILTERS.includes((country as TCountry) || "")
                  ? (country as TCountry)
                  : "Switzerland";
              }

              if (isAdmin) {
                state.filters.ordering = "-user__last_app_login";
              }
            },
            false,
            "influencerNetworkStore/setInitialFilters",
          );

          const searchParams = get().buildSearchParams();
          get().setSearchParams(searchParams);
        },
        setFilters: (key, value, options = {skipQuery: false}) => {
          get().debouncedSearchParams.cancel();

          set(
            state => {
              state.filters[key] = value;
            },
            false,
            "influencerNetworkStore/setFilters",
          );

          if (options.skipQuery) {
            return;
          }

          let searchParams = get().buildSearchParams();
          let timeout = 1000;
          const cachedQuery = queryClient.getQueryData<
            InfiniteData<AxiosResponse<TPaginated<InfluencerProfileDetailSerializer>>>
          >(INFLUENCER_KEYS.qInfluencerList(searchParams));

          if (!!cachedQuery?.pages) {
            timeout = 0;
          }

          set(
            state => {
              state.debouncedSearchParams = debounce(() => {
                get().setSearchParams(searchParams);
              }, timeout);
            },
            false,
            "influencerNetworkStore/setDebouncedSearchParams",
          );

          get().debouncedSearchParams();
        },
        setValue: (key, value) =>
          set(
            state => {
              state[key] = value;
            },
            false,
            "influencerNetworkStore/setValue",
          ),

        getHistogram: key => get().initialFilters[key],
        reset: () => {
          set(initialState, false, "influencerNetworkStore/reset");
        },
      }),
      {name: "useInfluencerNetworkStore"},
    ),
  ),
);

export default useInfluencerNetworkStore;
